import React from 'react';
import styled from 'styled-components';
import SESLogs from 'admin/pages/SESLogs';
import Deliveries from 'admin/pages/Deliveries';
import Contributions from '../Contributions';
import FeedbackDashboard from 'donor/portal/pages/FeedbackDashboard';
import Notifications from '../Notifications';
import Organization from '../Organization';
import QuicksightAdmin from 'admin/pages/QuicksightAdmin';
import ManageAccounts from 'admin/pages/ManageAccounts';
import Indicators from 'admin/pages/Indicators';
import ServiceCollections from 'admin/pages/ServiceCollections';
import Services from 'admin/pages/Services';
import { useHistory } from 'react-router-dom';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBuildingColumns,
  faCampfire,
  faComment,
  faEnvelope,
  faEnvelopeOpenText,
  faEye,
  faFileLines,
  faFileUser,
  faFlag,
  faFlask,
  faGlasses,
  faHandHoldingDollar,
  faLayerGroup,
  faList,
  faSackDollar,
  faSitemap,
  faStar,
  faTruckFast,
  faUser,
  faUsers,
  faInbox,
  faPeopleGroup,
  faRobot,
} from '@fortawesome/pro-light-svg-icons';
import ManageUsers from '../ManageUsers';
import MyAccount from '../MyAccount';
import BetaFeedback from 'npo/pages/BetaFeedback';
import ReportSharing from '../reportSharing';
import ReportRatings from 'admin/pages/ReportRatings';
import FeatureFlags from 'admin/pages/FeatureFlags';
import CITests from 'admin/pages/CITests';
import ProgramProfilingAdmin from 'admin/pages/ProgramProfiling';
import PartnerQueue from 'admin/pages/PartnerQueue';
import OverAllocatedInvestments from 'admin/pages/OverAllocatedInvestments';
import BbClientManagement from 'admin/pages/BbClientManagement';
import IrsManagement from 'admin/pages/IrsManagement';
import HafizAiAgent from 'admin/pages/HafizAiAgent';

type OptionBase = {
  path: string;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.FC<any>;
  icon?: IconProp | IconDefinition;
};

type OptionWithShowOnTop = OptionBase & {
  showOnTop: true;
  index?: number | undefined;
};

type OptionWithOutShowOnTop = OptionBase & {
  showOnTop?: false;
  hideIcon?: boolean;
};

type Option = OptionWithShowOnTop | OptionWithOutShowOnTop;

export const routes = {
  manageAccounts: {
    path: '/account/manage-accounts',
    component: ManageAccounts,
    title: 'Manage Accounts',
    icon: faFileUser,
    showOnTop: true,
    index: 100,
  },
  feedback: {
    title: 'Feedback',
    path: '/account/feedback',
    component: FeedbackDashboard,
    icon: faComment,
    showOnTop: true,
    index: 100,
  },
  betaFeedback: {
    title: 'Beta Feedback',
    path: '/account/beta-feedback',
    component: BetaFeedback,
    icon: faComment,
    showOnTop: false,
    index: 100,
  },
  globalResources: {
    path: '/account/indicators',
    component: Indicators,
    title: 'Global Resources',
    icon: faCampfire,
  },
  sesLogs: {
    title: 'Email Logs',
    path: '/account/ses-logs',
    component: SESLogs,
    icon: faTruckFast,
  },
  notificationRules: {
    title: 'Notification Rules',
    path: '/account/notifications',
    component: Notifications,
    icon: faEnvelope,
    hideIcon: true,
  },
  notificationLogs: {
    title: 'Notification Deliveries',
    path: '/account/notifications_logs',
    component: Deliveries,
    icon: faEnvelopeOpenText,
  },
  contributions: {
    title: 'Donations',
    path: '/account/contribution',
    component: Contributions,
    icon: faHandHoldingDollar,
    hideIcon: true,
  },
  organization: {
    path: '/account/organization',
    title: 'My Organization',
    component: Organization,
    icon: faSitemap,
    hideIcon: true,
  },
  reportSharing: {
    path: '/account/report-sharing',
    title: 'Reports Visibility',
    component: ReportSharing,
    icon: faEye,
    hideIcon: true,
  },
  quickSight: {
    path: '/account/quicksight',
    component: QuicksightAdmin,
    title: 'QuickSight',
    icon: faGlasses,
    hideIcon: true,
  },
  impactModel: {
    path: '/account/impactModels',
    component: Services,
    title: 'Impact Models',
    icon: faLayerGroup,
    hideIcon: true,
  },
  causeCategories: {
    path: '/account/causeCategories',
    component: ServiceCollections,
    title: 'Cause Categories',
    icon: faList,
    hideIcon: true,
  },
  manageUsers: {
    path: '/account/manage-users',
    component: ManageUsers,
    title: 'Manage Users',
    icon: faUsers,
  },
  myAccount: {
    path: '/account/my-account',
    component: MyAccount,
    title: 'My Account',
    icon: faUser,
  },
  reportRatings: {
    path: '/account/report-ratings',
    component: ReportRatings,
    title: 'Report Ratings',
    icon: faStar,
  },
  featureFlags: {
    path: '/account/feature-flags',
    component: FeatureFlags,
    title: 'Feature Flags',
    icon: faFlag,
  },
  ciTests: {
    path: '/account/ci-tests',
    component: CITests,
    title: 'CI Tests',
    icon: faFlask,
  },
  programProfiling: {
    path: '/account/program-profiling',
    component: ProgramProfilingAdmin,
    title: 'Program Profiles',
    icon: faFileLines,
  },
  partnerQueue: {
    path: '/account/partner-queue',
    component: PartnerQueue,
    title: 'Partner Queue',
    icon: faInbox,
  },
  overAllocatedInvestments: {
    path: '/account/over-allocated-investments',
    component: OverAllocatedInvestments,
    title: 'Investments',
    icon: faSackDollar,
  },
  bbClientManagement: {
    path: '/account/ti-client-management',
    component: BbClientManagement,
    title: 'Client Management',
    icon: faPeopleGroup,
  },
  irsManagement: {
    path: '/account/irs-management',
    component: IrsManagement,
    title: 'IRS Management',
    icon: faBuildingColumns,
  },
  hafizAiAgent: {
    path: '/account/hafiz-ai-agent',
    component: HafizAiAgent,
    title: 'Hafiz\'s AI Agent',
    icon: faRobot,
  },
};

export const superAdminOptions: Option[] = [
  routes.manageAccounts,
  routes.feedback,
  routes.globalResources,
  routes.sesLogs,
  routes.notificationRules,
  routes.contributions,
  routes.organization,
  routes.quickSight,
  routes.impactModel,
  routes.causeCategories,
  routes.reportRatings,
  routes.featureFlags,
  routes.ciTests,
  routes.programProfiling,
  routes.partnerQueue,
  routes.overAllocatedInvestments,
  routes.bbClientManagement,
  routes.irsManagement,
  routes.hafizAiAgent,
  routes.myAccount,
];

// A primary (header) tab that points at a route path/pattern.
export type PrimaryNavItem = { value: string; label: string };

// Single source of truth for each user type's navigation.
// `secondary` is shared by the secondary navbar (RouteTabs) and the account
// dropdown (AccountMenu) so the two always stay in sync.
// `primary` describes the header tabs for the types whose tabs are static.
// NPO's primary tabs are intentionally omitted here because they depend on
// runtime flags (Reports path/label, Donations visibility) and are rendered
// dynamically in NpoNavTabs.
export const navigationOptions: {
  npo: { secondary: Option[]; secondaryWithReportSharing: Option[] };
  donor: { primary: PrimaryNavItem[]; secondary: Option[] };
  admin: { primary: PrimaryNavItem[]; secondary: Option[] };
} = {
  npo: {
    secondary: [routes.myAccount, routes.manageUsers, routes.organization],
    secondaryWithReportSharing: [
      routes.myAccount,
      routes.manageUsers,
      routes.organization,
      routes.reportSharing,
    ],
  },
  donor: {
    primary: [
      { value: '/donor/home', label: 'Donations' },
      { value: '/donor/feedback', label: 'Feedback' },
      { value: '/donor/quicksight', label: 'Analytics' },
      { value: '/donor/strategy', label: 'Strategy' },
    ],
    secondary: [
      routes.myAccount,
      routes.manageUsers,
      routes.organization,
      routes.notificationRules,
    ],
  },
  admin: {
    primary: [
      { value: '/account/manage-accounts', label: 'Manage Accounts' },
      { value: '/account/feedback', label: 'Feedback' },
      { value: '/account/more-options', label: '+ More Options' },
    ],
    secondary: superAdminOptions,
  },
};

const StyledDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  max-width: 1200px;
  margin: auto;
  gap: ${SPACERS.medium};
  padding: ${SPACERS.extraLarge};
  & > div {
    width: 250px;
  }
`;

function isOptionWithOutShowOnTop(
  option: Option,
): option is OptionWithOutShowOnTop {
  return !option.showOnTop && option.title !== undefined;
}

const MoreOptions: React.FC = () => {
  const { push } = useHistory();
  const options: OptionWithOutShowOnTop[] = superAdminOptions.filter(
    (x): x is OptionWithOutShowOnTop =>
      isOptionWithOutShowOnTop(x) && !x.hideIcon,
  );

  return (
    <StyledDiv>
      <SelectorOptions
        variant="primary-radio"
        options={options}
        getLabel={o => o.title}
        getSelected={() => false}
        onClick={o => push(o.path)}
        getIcon={o => o.icon}
        margin="0"
        className="super-admin-options"
      />
    </StyledDiv>
  );
};

export default MoreOptions;
